/**
 * Custom Size Measurement Handler
 * Handles custom size selection and measurement form logic
 */
class CustomSizeHandler {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.formId = `product-form-${sectionId}`;
    this.measurementsContainer = document.getElementById(`custom-size-measurements-${sectionId}`);
    this.init();
  }

  init() {
    if (!this.measurementsContainer) return;

    // Start with accordion collapsed
    this.measurementsContainer.classList.remove('accordion-expanded');
    this.measurementsContainer.classList.remove('hidden');
    
    // Check if user has opened this before (localStorage)
    const hasOpenedBefore = localStorage.getItem(`custom-size-opened-${this.sectionId}`);
    if (hasOpenedBefore) {
      this.measurementsContainer.classList.add('animation-disabled');
    }
    
    // Set initial accordion state
    const content = this.measurementsContainer.querySelector('.custom-size-accordion-content');
    const header = this.measurementsContainer.querySelector('.custom-size-accordion-header');
    if (content && header) {
      content.setAttribute('aria-hidden', 'true');
      header.setAttribute('aria-expanded', 'false');
    }

    // Find all size option radios (including Custom)
    // We need to find size options more specifically - look for inputs in size-related fieldsets
    const sizeFieldsets = document.querySelectorAll('.product-form__input_size fieldset, fieldset.product-form__input_size');
    let sizeRadios = [];
    sizeFieldsets.forEach(fieldset => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      sizeRadios = [...sizeRadios, ...radios];
    });
    
    // Also find by name pattern
    const radiosByName = document.querySelectorAll(`input[type="radio"][name*="Size"], input[type="radio"][name*="size"]`);
    sizeRadios = [...new Set([...sizeRadios, ...radiosByName])];
    
    const customSizeRadio = document.querySelector(`input[data-custom-size="true"], input#${this.sectionId.replace(/[^a-zA-Z0-9]/g, '-')}-custom`);
    
    // Listen for size option changes - use capture phase to run BEFORE theme's handlers
    sizeRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const isCustom = radio.value === 'Custom' || radio.hasAttribute('data-custom-size');
        if (isCustom) {
          // CRITICAL: Set variant IMMEDIATELY before theme logic runs
          this.ensureValidVariant();
          // Also run handleSizeChange which will expand accordion
          this.handleSizeChange(radio);
        } else {
          this.handleSizeChange(radio);
        }
      }, true); // Use capture phase - runs BEFORE theme handlers
    });

    if (customSizeRadio) {
      customSizeRadio.addEventListener('change', (e) => {
        // CRITICAL: Set variant IMMEDIATELY before theme logic runs
        this.ensureValidVariant();
        this.handleCustomSizeSelect();
      }, true); // Use capture phase
    }
    
    // Also listen on click (before change event) for even earlier intervention
    sizeRadios.forEach(radio => {
      radio.addEventListener('click', (e) => {
        const isCustom = radio.value === 'Custom' || radio.hasAttribute('data-custom-size');
        if (isCustom) {
          // Set variant on click (before change event fires)
          setTimeout(() => {
            this.ensureValidVariant();
          }, 0);
        }
      }, true);
    });

    // Accordion toggle
    const accordionHeader = this.measurementsContainer.querySelector('.custom-size-accordion-header');
    if (accordionHeader) {
      accordionHeader.addEventListener('click', () => this.toggleAccordion());
    }

    // Top/Bottom section toggle
    const sectionButtons = this.measurementsContainer.querySelectorAll('.custom-size-section-btn');
    sectionButtons.forEach(btn => {
      btn.addEventListener('click', () => this.switchMeasurementSection(btn));
    });

    // Number-only input validation
    const measurementInputs = this.measurementsContainer.querySelectorAll('.custom-measurement-input');
    measurementInputs.forEach(input => {
      input.addEventListener('input', (e) => this.validateNumberInput(e.target));
      input.addEventListener('keypress', (e) => this.preventNonNumeric(e));
    });

    // Handle form submission - ensure measurements are included
    const productForm = document.getElementById(this.formId);
    if (productForm) {
      productForm.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    // Monitor variant changes to keep buttons enabled when Custom is selected
    this.monitorVariantChanges();
  }

  monitorVariantChanges() {
    // Watch for variant selection changes and ensure buttons stay enabled for Custom
    const variantInput = document.querySelector(`#${this.formId} input[name="id"]`);
    if (!variantInput) return;

    // Use MutationObserver to watch for disabled attribute changes on buttons
    const productForm = document.getElementById(this.formId);
    if (!productForm) return;

    const addButton = productForm.querySelector('button[name="add"], .product-form__submit, .product_submit_button');
    const buyNowButton = productForm.querySelector('.product_buy_now_button');

    if (addButton) {
      const observer = new MutationObserver(() => {
        const customSizeRadio = document.querySelector(`input[data-custom-size="true"]:checked, input[value="Custom"]:checked`);
        if (customSizeRadio) {
          // Re-enable if Custom is selected - check both disabled attribute and text content
          const btnLabel = addButton.querySelector('.btn-label, .hidden-on-load, .btn-action');
          const isUnavailable = addButton.hasAttribute('disabled') || 
                               (btnLabel && (btnLabel.textContent.toLowerCase().includes('unavailable') || 
                                            btnLabel.textContent.toLowerCase().includes('sold out')));
          
          if (isUnavailable) {
            // Immediately fix it - no delay
            this.ensureValidVariant();
            this.enableButtons();
          }
        }
      });

      observer.observe(addButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
      
      // Also observe the button's parent to catch any changes
      const buttonParent = addButton.closest('.product-form__buttons, .button_buy-now, .product-detail__buy-buttons');
      if (buttonParent) {
        observer.observe(buttonParent, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }
    }
    
    // Set up a periodic check to ensure buttons stay enabled when Custom is selected
    // This is a fallback in case MutationObserver misses something
    const checkInterval = setInterval(() => {
      const customSizeRadio = document.querySelector(`input[data-custom-size="true"]:checked, input[value="Custom"]:checked`);
      if (customSizeRadio && addButton) {
        const btnLabel = addButton.querySelector('.btn-label, .hidden-on-load, .btn-action');
        const isUnavailable = addButton.hasAttribute('disabled') || 
                             (btnLabel && (btnLabel.textContent.toLowerCase().includes('unavailable') || 
                                          btnLabel.textContent.toLowerCase().includes('sold out') ||
                                          btnLabel.textContent.toLowerCase().includes('out of stock')));
        
        if (isUnavailable) {
          this.ensureValidVariant();
          this.enableButtons();
        }
      } else if (!customSizeRadio) {
        // If Custom is not selected, clear the interval to save resources
        clearInterval(checkInterval);
      }
    }, 300);
    
    // Store interval ID for cleanup if needed
    this.buttonCheckInterval = checkInterval;
  }

  handleSizeChange(radio) {
    const isCustom = radio.value === 'Custom' || radio.hasAttribute('data-custom-size');
    
    if (isCustom) {
      this.expandAccordion();
      // Ensure variant and buttons are enabled
      setTimeout(() => {
        this.ensureValidVariant();
        this.enableButtons();
      }, 10);
    } else {
      this.collapseAccordion();
      this.clearMeasurements();
      // Re-enable buttons when switching back to regular size
      setTimeout(() => {
        this.enableButtons();
      }, 10);
    }
  }

  handleCustomSizeSelect() {
    this.expandAccordion();
    // Ensure variant and buttons are enabled
    setTimeout(() => {
      this.ensureValidVariant();
      this.enableButtons();
    }, 10);
  }

  ensureValidVariant() {
    // Find the variant input in the form
    const variantInput = document.querySelector(`#${this.formId} input[name="id"]`);
    if (!variantInput) return;

    // Check if Custom is actually selected (check both checked state and value)
    const customSizeRadio = document.querySelector(`input[data-custom-size="true"]:checked, input[value="Custom"]:checked`);
    const customSizeLabel = document.querySelector(`label[for*="custom"]`);
    const isCustomSelected = customSizeRadio || 
                             (customSizeLabel && customSizeLabel.classList.contains('active')) ||
                             document.querySelector('.custom-size-label.active');
    
    if (!isCustomSelected && !customSizeRadio) {
      // If Custom is not selected, ensure buttons are still enabled for regular variants
      this.enableButtons();
      return;
    }

    // Get all variants from the JSON script tag (look for the one in variant picker area)
    const variantPickers = document.querySelectorAll('.product-detail__variant-picker script[type="application/json"]');
    let variantsScript = null;
    
    // Find the script that contains variant data
    for (const script of variantPickers) {
      try {
        const test = JSON.parse(script.textContent);
        if (Array.isArray(test) && test.length > 0 && test[0].id) {
          variantsScript = script;
          break;
        }
      } catch (e) {
        continue;
      }
    }
    
    if (!variantsScript) {
      // Fallback: try to find any script with variants
      const allScripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of allScripts) {
        try {
          const test = JSON.parse(script.textContent);
          if (Array.isArray(test) && test.length > 0 && test[0].id) {
            variantsScript = script;
            break;
          }
        } catch (e) {
          continue;
        }
      }
    }
    
    if (!variantsScript) return;

    try {
      const variants = JSON.parse(variantsScript.textContent);
      // Find the first available variant, or use the currently selected one if it's valid
      const currentVariantId = parseInt(variantInput.value);
      const currentVariant = variants.find(v => v.id === currentVariantId);
      
      let targetVariant;
      if (currentVariant && currentVariant.available) {
        // Keep current variant if it's available
        targetVariant = currentVariant;
      } else {
        // Find first available variant (prefer XL, then L, then first available)
        const xlVariant = variants.find(v => v.available && (v.option1 === 'XL' || v.option2 === 'XL' || v.option3 === 'XL'));
        const lVariant = variants.find(v => v.available && (v.option1 === 'L' || v.option2 === 'L' || v.option3 === 'L'));
        targetVariant = xlVariant || lVariant || variants.find(v => v.available) || variants[0];
      }
      
      if (targetVariant) {
        // Always ensure a valid variant is set when Custom is selected
        if (variantInput.value !== targetVariant.id.toString()) {
          // Store the original value before changing
          if (!variantInput.hasAttribute('data-original-variant-id')) {
            variantInput.setAttribute('data-original-variant-id', variantInput.value);
          }
          variantInput.value = targetVariant.id;
          
          // Trigger change event to update variant selection logic
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          variantInput.dispatchEvent(changeEvent);
        }
        
        // Ensure price is visible and updated
        this.updatePriceDisplay(targetVariant);
        
        // Ensure buttons are enabled IMMEDIATELY and multiple times to override theme logic
        this.enableButtons(); // Run immediately
        
        setTimeout(() => {
          this.enableButtons();
        }, 10);
        
        setTimeout(() => {
          this.enableButtons();
        }, 50);
        
        // Also run after a longer delay to catch any late-running theme logic
        setTimeout(() => {
          this.enableButtons();
        }, 200);
        
        // Run one more time to be safe
        setTimeout(() => {
          this.enableButtons();
        }, 500);
        
        // Final check after theme logic should have finished
        setTimeout(() => {
          this.enableButtons();
        }, 1000);
      }
    } catch (e) {
      console.warn('Could not parse variants JSON:', e);
    }
  }

  updatePriceDisplay(variant) {
    if (!variant) return;
    
    // Ensure price section is visible
    const priceSection = document.getElementById(`price-${this.sectionId}`);
    if (priceSection) {
      priceSection.classList.remove('visibility-hidden', 'hidden');
      priceSection.style.display = '';
    }
    
    // Find all price elements and ensure they're visible
    const productTarget = document.querySelector(`#${this.formId}`).closest('.product__item-js, .product-detail, .product-form, .product-detail__information');
    if (!productTarget) {
      // Fallback: try to find price container directly
      const priceContainer = document.querySelector('.card-product-price, .product-price, #price-' + this.sectionId);
      if (priceContainer) {
        priceContainer.classList.remove('visibility-hidden', 'hidden');
        priceContainer.style.display = '';
      }
      return;
    }
    
    // Update price display using theme's format
    if (window.Shopify && window.themeGlobalVariables) {
      const priceFormat = Shopify.formatMoney(
        variant.price,
        window.themeGlobalVariables.settings.money_format
      );
      
      // Update regular price - try multiple selectors
      const priceSelectors = [
        '.price-regular .price',
        '.card-product-price .price',
        '.price',
        '.product-price .price'
      ];
      
      priceSelectors.forEach(selector => {
        const priceEl = productTarget.querySelector(selector);
        if (priceEl && !priceEl.closest('.compare-price')) {
          priceEl.innerHTML = priceFormat;
          priceEl.closest('.card-product-price, .price-regular, .product-price')?.classList.remove('visibility-hidden', 'hidden');
        }
      });
      
      // Update compare at price if exists
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const comparePriceFormat = Shopify.formatMoney(
          variant.compare_at_price,
          window.themeGlobalVariables.settings.money_format
        );
        const comparePriceSelectors = ['.compare-price', '.price-item', 's.price-item'];
        comparePriceSelectors.forEach(selector => {
          const comparePriceEl = productTarget.querySelector(selector);
          if (comparePriceEl) {
            comparePriceEl.innerHTML = comparePriceFormat;
            comparePriceEl.style.display = '';
          }
        });
      }
      
      // Ensure price container is visible
      const priceContainers = productTarget.querySelectorAll('.card-product-price, .product-price, #price-' + this.sectionId);
      priceContainers.forEach(container => {
        container.style.display = '';
        container.classList.remove('visibility-hidden', 'hidden');
      });
    }
    
    // Also trigger theme's price update if available
    setTimeout(() => {
      // Look for variant picker and trigger its updatePrice method
      const variantPicker = document.querySelector(`variant-radios-detail[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`);
      if (variantPicker && variantPicker.updatePrice) {
        variantPicker.updatePrice(productTarget);
      }
    }, 100);
  }

  enableButtons() {
    // Find and enable Add to Cart and Buy Now buttons
    const productForm = document.getElementById(this.formId);
    if (!productForm) return;

    const addButton = productForm.querySelector('button[name="add"], .product-form__submit, .product_submit_button');
    const buyNowButton = productForm.querySelector('.product_buy_now_button');
    
    // Check if Custom is selected
    const customSizeRadio = document.querySelector(`input[data-custom-size="true"]:checked, input[value="Custom"]:checked`);
    
    if (addButton) {
      // Always remove disabled attribute
      addButton.removeAttribute('disabled');
      
      // Update button text if Custom is selected
      if (customSizeRadio) {
        // Get the add to cart text from theme strings
        const addToCartText = window.variantStrings?.addToCart || 'Add to Cart';
        
        // Update all possible label selectors
        const labelSelectors = ['.btn-label', '.hidden-on-load', '.btn-action', 'span'];
        labelSelectors.forEach(selector => {
          const labels = addButton.querySelectorAll(selector);
          labels.forEach(label => {
            const text = label.textContent.trim().toLowerCase();
            // Update if it says unavailable, sold out, or out of stock
            if (text.includes('unavailable') || 
                text.includes('sold out') || 
                text.includes('out of stock') ||
                text.includes('outstock')) {
              label.textContent = addToCartText;
            }
          });
        });
        
        // Also check the button's direct text content
        if (addButton.textContent) {
          const btnText = addButton.textContent.trim().toLowerCase();
          if (btnText.includes('unavailable') || btnText.includes('sold out')) {
            // Find the label span and update it
            const label = addButton.querySelector('.btn-label, .hidden-on-load');
            if (label) {
              label.textContent = addToCartText;
            }
          }
        }
      }
    }
    
    if (buyNowButton) {
      buyNowButton.removeAttribute('disabled');
    }
  }

  expandAccordion() {
    if (this.measurementsContainer) {
      this.measurementsContainer.classList.remove('hidden');
      this.measurementsContainer.classList.add('accordion-expanded');
      
      // Disable animation permanently after first open
      if (!this.measurementsContainer.classList.contains('animation-disabled')) {
        this.measurementsContainer.classList.add('animation-disabled');
        // Store in localStorage so animation doesn't play on future page loads
        localStorage.setItem(`custom-size-opened-${this.sectionId}`, 'true');
      }
      
      const content = this.measurementsContainer.querySelector('.custom-size-accordion-content');
      const header = this.measurementsContainer.querySelector('.custom-size-accordion-header');
      const arrow = header?.querySelector('.accordion-arrow');
      
      if (content && header) {
        content.setAttribute('aria-hidden', 'false');
        header.setAttribute('aria-expanded', 'true');
        if (arrow) arrow.style.transform = 'rotate(180deg)';
      }
      
      // Enable all measurement inputs
      const inputs = this.measurementsContainer.querySelectorAll('.custom-measurement-input');
      inputs.forEach(input => {
        input.disabled = false;
      });
      // Ensure warning input and size property are enabled
      const warningInput = this.measurementsContainer.querySelector('.custom-size-warning-input');
      const sizePropertyInput = this.measurementsContainer.querySelector('.custom-size-property-input');
      if (warningInput) {
        warningInput.disabled = false;
      }
      if (sizePropertyInput) {
        sizePropertyInput.disabled = false;
      }
    }
  }

  collapseAccordion() {
    if (this.measurementsContainer) {
      this.measurementsContainer.classList.remove('accordion-expanded');
      
      const content = this.measurementsContainer.querySelector('.custom-size-accordion-content');
      const header = this.measurementsContainer.querySelector('.custom-size-accordion-header');
      const arrow = header?.querySelector('.accordion-arrow');
      
      if (content && header) {
        content.setAttribute('aria-hidden', 'true');
        header.setAttribute('aria-expanded', 'false');
        if (arrow) arrow.style.transform = 'rotate(0deg)';
      }
      
      // Disable warning input and size property when not custom
      const warningInput = this.measurementsContainer.querySelector('.custom-size-warning-input');
      const sizePropertyInput = this.measurementsContainer.querySelector('.custom-size-property-input');
      if (warningInput) {
        warningInput.disabled = true;
      }
      if (sizePropertyInput) {
        sizePropertyInput.disabled = true;
      }
    }
  }

  clearMeasurements() {
    const inputs = this.measurementsContainer.querySelectorAll('.custom-measurement-input');
    inputs.forEach(input => {
      input.value = '';
      input.disabled = true;
    });
  }

  toggleAccordion() {
    const header = this.measurementsContainer.querySelector('.custom-size-accordion-header');
    const content = this.measurementsContainer.querySelector('.custom-size-accordion-content');
    const arrow = header.querySelector('.accordion-arrow');
    
    if (!content || !header) return;

    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      content.style.maxHeight = '0';
      content.style.opacity = '0';
      header.setAttribute('aria-expanded', 'false');
      content.setAttribute('aria-hidden', 'true');
      if (arrow) arrow.style.transform = 'rotate(0deg)';
    } else {
      content.style.maxHeight = content.scrollHeight + 'px';
      content.style.opacity = '1';
      header.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      if (arrow) arrow.style.transform = 'rotate(180deg)';
    }
  }

  switchMeasurementSection(button) {
    const section = button.getAttribute('data-section');
    const sections = this.measurementsContainer.querySelectorAll('.custom-size-section');
    const buttons = this.measurementsContainer.querySelectorAll('.custom-size-section-btn');

    // Update button states
    buttons.forEach(btn => {
      btn.classList.remove('active');
      if (btn === button) btn.classList.add('active');
    });

    // Update section visibility
    sections.forEach(sec => {
      if (sec.getAttribute('data-measurement-section') === section) {
        sec.classList.remove('hidden');
        sec.classList.add('active');
      } else {
        sec.classList.add('hidden');
        sec.classList.remove('active');
      }
    });
  }

  validateNumberInput(input) {
    // Remove any non-numeric characters except decimal point
    let value = input.value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    input.value = value;
  }

  preventNonNumeric(e) {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  }

  handleFormSubmit(e) {
    // Check if Custom size is selected
    const customSizeRadio = document.querySelector(`input[data-custom-size="true"]:checked, input[value="Custom"]:checked`);
    
    if (customSizeRadio && this.measurementsContainer) {
      // Ensure a valid variant is selected before submission
      this.ensureValidVariant();
      
      // Enable all measurement inputs before submission
      const inputs = this.measurementsContainer.querySelectorAll('.custom-measurement-input');
      inputs.forEach(input => {
        input.disabled = false;
        // Remove name if empty to avoid submitting empty properties
        if (!input.value || input.value.trim() === '') {
          const originalName = input.getAttribute('data-original-name') || input.name;
          if (!input.hasAttribute('data-original-name')) {
            input.setAttribute('data-original-name', originalName);
          }
          input.name = ''; // Remove name so it won't be submitted
        } else {
          // Restore name if it was removed
          if (input.hasAttribute('data-original-name')) {
            input.name = input.getAttribute('data-original-name');
          }
        }
      });
      
      // Ensure warning input and size property are enabled
      const warningInput = this.measurementsContainer.querySelector('.custom-size-warning-input');
      const sizePropertyInput = this.measurementsContainer.querySelector('.custom-size-property-input');
      if (warningInput) {
        warningInput.disabled = false;
      }
      if (sizePropertyInput) {
        sizePropertyInput.disabled = false;
      }

      // Ensure buttons are enabled
      this.enableButtons();
    } else {
      // Disable measurement inputs if not custom size
      if (this.measurementsContainer) {
        const inputs = this.measurementsContainer.querySelectorAll('.custom-measurement-input');
        inputs.forEach(input => {
          input.disabled = true;
          // Store original name before removing
          if (!input.hasAttribute('data-original-name') && input.name) {
            input.setAttribute('data-original-name', input.name);
          }
          input.name = ''; // Remove name so it won't be submitted
        });
        
        const warningInput = this.measurementsContainer.querySelector('.custom-size-warning-input');
        const sizePropertyInput = this.measurementsContainer.querySelector('.custom-size-property-input');
        if (warningInput) {
          warningInput.disabled = true;
        }
        if (sizePropertyInput) {
          sizePropertyInput.disabled = true;
        }
      }
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Find all custom size measurement containers
  const containers = document.querySelectorAll('[id^="custom-size-measurements-"]');
  containers.forEach(container => {
    const sectionId = container.getAttribute('data-section-id');
    if (sectionId) {
      new CustomSizeHandler(sectionId);
    }
  });
});

// Also initialize when variant changes (for dynamic updates)
document.addEventListener('variant:change', (e) => {
  const sectionId = e.detail?.sectionId;
  if (sectionId) {
    const container = document.getElementById(`custom-size-measurements-${sectionId}`);
    if (container && !container.hasAttribute('data-initialized')) {
      container.setAttribute('data-initialized', 'true');
      new CustomSizeHandler(sectionId);
    }
  }
});
